import { SearchOutlined } from "@ant-design/icons";
import { searchBookings } from "apis/booking.api";
import { cancelTicketRequest } from "apis/ticket-cancelation.api";
import { bookingListResponse } from "mappers/booking.mapper";
import classnames from "classnames";
import {
  ActionButton,
  Button,
  Field,
  Form,
  Input,
  Modal,
  Title,
  Toast,
} from "components/commons";
import { VenueContext } from "contexts";
import { StyleType } from "enums";
import { useApi, useForm } from "hooks";
import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import lang from "translations";
import initialFormState from "./cancel-ticket-state";
import { mixpanel, TrackEvent } from "mixpanel";
import BookingSummary from "./booking-summary/booking-summary.module";

const CancelTicketModal = ({
  active,
  close,
  ok,
  initialState = undefined,
  onTicketCancelled,
  ...props
}) => {
  const { venue } = useContext(VenueContext);
  const { venueId } = venue;

  const [searchedKey, setSearchedKey] = useState("");
  const [cancelClicked, setCancelClicked] = useState(false);
  const [booking, setBooking] = useState(null);

  const formState = useMemo(() => initialFormState(initialState), [initialState]);

  const {
    fields,
    modifyField,
    submitForm,
    getFormValues,
    clearForm,
    applyFieldErrors,
    validateForm,
  } = useForm({ initialState: formState });

  const { request, loading, mappedData, result } = useApi({
    api: searchBookings,
    isArray: true,
    handleOwnError: false,
    mapper: bookingListResponse,
  });

  const findBookingByTicketId = (data, ticketId) => {
    if (!data || !Array.isArray(data.data)) return null;
    for (const booking of data.data) {
      for (const customer of booking.bookingCustomers) {
        if (customer.externalTicketId &&
            customer.externalTicketId.toLowerCase() === ticketId.toLowerCase()) {
          return booking;
        }
      }
    }
    return null;
  };

  useEffect(() => {
    const bookingFound = findBookingByTicketId(result, searchedKey);
    setBooking(bookingFound);
  }, [result, searchedKey]);

  const { request: CancelTicketRequest, loading: CancelTicketLoading } = useApi({
    api: cancelTicketRequest,
    params: {
      externalTicketId: searchedKey.toString().trim(),
      venueId,
    },
    handleOwnError: true,
  });

  const handleSubmit = useCallback(async (searchKey) => {
    try {
      await request({
        searchKey: searchKey.toString().trim(),
        venueId,
        page: 1,
        size: 100,
      });
    } catch ({ handleError }) {
      handleError();
    }
  }, [request,venueId]);

  const handleSearch = useCallback(() => {

    if (!loading && fields.searchKey.value) {
      setSearchedKey(fields.searchKey.value);
      submitForm(() => handleSubmit(fields.searchKey.value));
    }
  }, [handleSubmit, setSearchedKey, fields.searchKey, submitForm, loading]);

  const handleSearchChange = useCallback(
    (name, val) => {
      if (val && val.value) {
        const value = val.value;// .replace(/\W/g, "");
        modifyField(name, { value });
      } else {
        modifyField(name, { value: null });
      }
    },
    [modifyField]
  );

  

  const handleCancelTicketClicked = useCallback(async () => {
    try {
      const params = getFormValues();
      validateForm();

      if (!params.reason || params.reason.trim() === "") {
        applyFieldErrors({ reason: lang.thisFieldIsRequired });
        return;
      }

      params.externalTicketId = searchedKey.toString().trim();
      await CancelTicketRequest({ ...params, venueId });

      Toast({
        content: lang.ticketCancelled,
        success: true,
        icon: "check",
      }).open();
      close();
      setCancelClicked(false);
      setSearchedKey("");
      clearForm();

      if (onTicketCancelled) onTicketCancelled();
    } catch (error) {
      close();
      setCancelClicked(false);
      setSearchedKey("");
      clearForm();
       Toast({
        content: error?.messages,
        error: true,
        icon: "check",
      }).open();
      console.error(error);
    }
  }, [
    CancelTicketRequest,
    getFormValues,
    venueId,
    applyFieldErrors,
    onTicketCancelled,
    validateForm,
    clearForm,
    searchedKey,
    close
  ]);

  return (
    <Modal
      width={1200}
      {...props}
      noHeader
      active={active}
      actionContent={
        <ActionButton
          className="px-md pb-md"
          secondary={{
            text: lang.discard,
            onClick: () => {
              mixpanel.track(TrackEvent.ClickedButton, {
                button: lang.cancelTicket,
              });

              if (cancelClicked) {
                setCancelClicked(false);
              } else {
                setSearchedKey("");
                clearForm();
                close();
              }
            },
          }}
          primary={
            booking
              ?
              {
                loading: CancelTicketLoading,
                disabled: CancelTicketLoading,
                type: StyleType.Danger,
                text: lang.cancel,
                onClick: handleCancelTicketClicked,
              }
              : false
          }
        />
      }
    >
      {/* Title */}
      <Title lg className="mb-sm pt-md">
        {lang.CancelTicket}
      </Title>

      {/* 🧱 Layout: Stack vertically */}
      <div className="flex flex-col gap-6 w-full">
        {/* Row 1: Search Form */}
        {!cancelClicked && (
          <Form onSubmit={handleSearch} className="w-full">
            <p className="text-xs text-gray-500 m-0 font-semibold">{lang.ticketNo}</p>
            <Field {...fields.searchKey}>
              <div className="flex">
                <Input
                  required
                  {...fields.searchKey}
                  className={classnames("rounded-r-none mt-xs border-r-0")}
                  placeholder="e.g. 1234 5678 9012 3456"
                  iconPrefix={<SearchOutlined className="mr-sm" />}
                  onChange={handleSearchChange}
                />

                <Button
                  loading={loading}
                  disabled={
                    loading || fields.searchKey.value === "" || !fields.searchKey.value
                  }
                  type={StyleType.Secondary}
                  className={classnames(
                    "rounded-l-none bg-gradient-to-b from-white to-white-dark border-blue-lightest mt-xs"
                  )}
                  onClick={handleSearch}
                >
                  {lang.search}
                </Button>
              </div>
            </Field>
          </Form>
        )}

        {/* Row 2: Booking Summary or Message */}
        {searchedKey && !loading && mappedData && (
          <>
            {booking?.bookingNumber ? (
              <div className="bg-blue-opaque px-md py-md rounded w-full">
                <BookingSummary data={booking}  externalTicketId={searchedKey}/>
                <Form onSubmit={handleSearch} className="w-full">
                  <Field {...fields.reason}>
                    <Input
                      {...fields.reason}
                      placeholder={lang.chooseAReason}
                      onChange={modifyField}
                    />
                  </Field>
                </Form>
              </div>

            ) : (
              <div className="bg-blue-opaque px-md py-md rounded w-full">
                <p className="text-sm">
                  {lang.noRecordsOf}{" "}
                  <span className="font-semibold">{searchedKey}</span>
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default CancelTicketModal;
